import React, { useEffect, useMemo, useState } from "react";
import { Card, Tabs, Button, Tag, Input, Select, message, Row, Col, Space, DatePicker, Upload, Result } from "antd";
import {
  PhoneOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  DollarOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
  WhatsAppOutlined,
  ArrowLeftOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  FileDoneOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  fetchCrmFieldsApi,
  fetchLeadsApi,
  fetchTasksApi,
  getLeadActivitiesApi,
  saveConversationApi,
  updateLeadCustomFieldApi,
  updateLeadMetaApi,
  fetchMyTeammatesApi,
} from "../../../../apiservice/crm";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeadPurpose, fetchLeads, fetchLeadSource, fetchLeadStatus } from "../leadThunk";
import CallTab from "./CallTab";
import { fetchDeals } from "../../Deals/dealThunk";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import TaskDrawer from "../../TodayTasks/components/TaskDrawer";
import CreateDealDrawer from "../../Deals/components/CreateDealDrawer";
import { convertToCompanyTimezoneObject, formatToCompanyTimezone, isAdmin } from "../../../../lib/functions";
import { fetchUsers } from "../../../AttendanceEntry/attendanceEntryThunk";
import { resolveDialableNumber, resolveWhatsAppNumber } from "../../../../lib/phone";
import AttachmentPreviewList from "../../../../components/AttachmentPreviewList";
import VoiceRecorder from "../../../../components/VoiceRecorder";
import "../../crmSharedStyle.css";

const { TextArea } = Input;

const stageColor = {
  prospecting: "orange",
  negotiation: "blue",
  won: "green",
  lost: "red",
};

const LeadDetail = () => {
  const { id } = useParams();

  const [lead, setLead] = useState(null);
  const [leadNotFound, setLeadNotFound] = useState(false);
  // const [deal, setDeal] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [text, setText] = useState("");
  const [customFields, setCustomFields] = useState([]); //custom fields we created from customize page
  const [editingField, setEditingField] = useState(null);  //for status and source editing
  const [tempValue, setTempValue] = useState(null);
  const dispatch = useDispatch();

  // const [callStatus, setCallStatus] = useState(null);
  // const [leadStatus, setLeadStatus] = useState(null);
  const [activityText, setActivityText] = useState("");
  const [activityFileList, setActivityFileList] = useState([]);

  const [taskDrawer, setTaskDrawer] = useState(false);
  const [dealDrawer, setDealDrawer] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const { list : dealList } = useSelector((state) => state.deals);
  const { list, statusList, sourceList, purposeList } = useSelector((state) => state.leads);
  const users = useSelector((s) => s.lookups.users);
  const authInfo = useSelector(selectAuthInfo);
  const adminUser = isAdmin(authInfo);
  const currency = authInfo?.currency;
  const navigate = useNavigate();

  // Self-service reassignment: a non-admin can reassign THIS lead to a
  // teammate if it's currently assigned to themselves. Loaded once, not
  // gated on the lead loading first, since it doesn't depend on it.
  const [myTeammates, setMyTeammates] = useState([]);
  useEffect(() => {
    if (adminUser) return;
    fetchMyTeammatesApi()
      .then((res) => setMyTeammates(res.data?.members || []))
      .catch(() => setMyTeammates([]));
  }, [adminUser]);

  // 🔹 Load activity timeline
  const loadActivities = async () => {
    try {
      setActivitiesLoading(true);

      const res = await getLeadActivitiesApi({
        lead_id: lead.id,
      });

      setActivities(res.data || []);
    } catch (err) {
      message.error("Failed to load activities");
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleTabChange = (key) => {
    // Notes and Activities are both rendered from the same activity feed
    // (just filtered differently -- see isNoteActivity below), so either
    // tab needs the same underlying fetch.
    if (key === "activity" || key === "notes") {
      loadActivities();
    }
  };

    useEffect(()=>{
      dispatch(fetchLeadSource());
      dispatch(fetchLeadStatus());
      dispatch(fetchLeadPurpose());
      if(users.length < 1 ){
      dispatch(fetchUsers());
      }
    },[])

    useEffect(() => {
      const loadFields = async () => {
        try {
          const res = await fetchCrmFieldsApi({ module: "lead" });         
          setCustomFields(res.data || []);
        } catch (err) {
          console.log(err);
        }
      };
      loadFields();
    }, []);


    useEffect(() => {
    if (!list.length) {
        dispatch(fetchLeads());
      }
      if(!dealList.length){
      dispatch(fetchDeals());
      }
    }, []);

  // The Leads list in Redux may be empty, paginated, or filtered down to a
  // different set than the lead this page was opened for (e.g. opened
  // directly from a Task's "Related Lead" card, or a different Leads-page
  // filter was active on a previous visit) -- list.find() alone can
  // legitimately come up empty even though the lead exists and is
  // accessible, which used to leave this page stuck on "Loading..."
  // forever. Fall back to fetching this one lead directly when that
  // happens, reusing the same /crm/leads endpoint with an `id` filter.
  useEffect(() => {
    const found = list?.find((l) => l.id === id);
    if (found) {
      // Always re-sync from `list`, even if this lead is already loaded --
      // e.g. a status/field edit here dispatches fetchLeads() to refresh the
      // list, and this page needs to pick up that fresh data instead of
      // keeping the stale `lead` state around (that was the actual bug:
      // editing a lead showed the old value until a full page reload).
      setLead(found);
      setLeadNotFound(false);
      return;
    }

    // Not in the cached list at all -- only worth a direct fetch if we
    // don't already have this exact lead loaded from a prior direct fetch
    // (avoids re-fetching every time `list` changes for a lead that will
    // never be in it, e.g. one opened from outside the Leads page).
    if (lead?.id === id) return;

    let cancelled = false;
    fetchLeadsApi({ id })
      .then((res) => {
        if (cancelled) return;
        const match = (res.data || [])[0];
        if (match) {
          setLead(match);
          setLeadNotFound(false);
        } else {
          setLeadNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLeadNotFound(true);
      });

    return () => { cancelled = true; };
  }, [list, id, lead, purposeList]);

const relatedDeals = useMemo(() => {
  return dealList.filter((d) => d.contact_id === id);
}, [dealList, id]);  


  const loadTasks = async () => {
    try {
      const res = await fetchTasksApi({
        lead_id: id,
      });

      setTasks(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };


  useEffect(() => {        
    loadTasks();
  }, [id]);

  const formatPhone = (phone) => {
    const parsed = parsePhoneNumberFromString(resolveDialableNumber(phone, lead?.country));
    return parsed ? parsed.formatInternational() : phone;
    };

  // 🔹 Add note
  const addNote = async () => {
    if (addingNote) return;
    // A voice note or other attachment is a complete activity on its own --
    // typed text was previously required even when there was nothing to
    // type (e.g. a voice-only note), silently no-op-ing the button.
    if (!activityText.trim() && !activityFileList.length) return;
try {
    setAddingNote(true);

    if (activityFileList.length) {
      const formData = new FormData();

      formData.append("customer_id", id);
      formData.append("text", activityText);

      activityFileList.forEach((f) => {
        formData.append("attachments", f.originFileObj || f);
      });

      await saveConversationApi(formData);
    } else {
      await saveConversationApi({
        customer_id: id,
        text:activityText,
      });
    }

    message.success("Note added");
    setActivityText("");
    setActivityFileList([]);
    loadActivities();
     } catch (error) {
    console.error("Failed to add note", error);
    message.error(
      error?.response?.data?.message || "Failed to add note"
    );
  } finally {
    setAddingNote(false);
  }
  };

  const getActivityMeta = (a) => {
  if (a.type === "call") {
    return { icon: <PhoneOutlined />, color: "#1677ff" };
  }

  if (a.source === "task") {
    return { icon: <CheckCircleOutlined />, color: "#fa8c16" };
  }

  if (a.source === "deal") {
    return { icon: <DollarOutlined />, color: "#722ed1" };
  }

  if (a.type === "system") {
    return { icon: <UserOutlined />, color: "#13c2c2" };
  }

  if (a.source === "transaction") {
    if (a.type === "sale" || a.type === "sale_pharma") {
      return { icon: <ShoppingCartOutlined />, color: "#eb2f96" };
    }
    if (a.type === "sales_order") {
      return { icon: <ShoppingOutlined />, color: "#2f54eb" };
    }
    if (a.type === "quotation") {
      return { icon: <FileDoneOutlined />, color: "#fa541c" };
    }
  }

  return { icon: <FileTextOutlined />, color: "#52c41a" };
};

// A manually-added note is a 'conversation' row saved with no explicit
// type override (backend defaults it to "note" -- see saveConversation in
// administration.js). Calls, system log entries, tasks, deals and
// transactions all come through this same combined feed but are tagged
// differently, so this is the one place that decides what belongs on the
// Notes tab vs. the Activities tab.
const isNoteActivity = (a) => a.source === "conversation" && a.type === "note";

// Where a transaction activity row navigates on click -- mirrors the exact
// Pharma-vs-normal voucher routing SalesRecordTable.js already uses.
const transactionActivityRoute = (a, authInfo) => {
  if (a.type === "sale_pharma" && authInfo?.branch_company_type === "Pharmacy") {
    return `/sales/quick-sales-pharma-voucher/${a.id}`;
  }
  if (a.type === "sale" || a.type === "sale_pharma") {
    return `/sales/quick-sales-voucher/${a.id}`;
  }
  if (a.type === "sales_order") {
    return `/order/quick-sales-order-voucher/${a.id}`;
  }
  if (a.type === "quotation") {
    return `/quotation/quick-quotation-voucher/${a.id}`;
  }
  return null;
};


  const groupByDate = (list) => {
    const groups = {};

    list.forEach((a) => {
      const date = convertToCompanyTimezoneObject(a.created_at).format("DD MMM YYYY");
      if (!groups[date]) groups[date] = [];
      groups[date].push(a);
    });

    return groups;
  };

  const groupedNotes = useMemo(
    () => groupByDate(activities.filter(isNoteActivity)),
    [activities],
  );

  const groupedActivities = useMemo(
    () => groupByDate(activities.filter((a) => !isNoteActivity(a))),
    [activities],
  );

  // Shared timeline renderer for both the Notes and Activities tabs -- same
  // date-grouped layout, just fed a differently-filtered list.
  const renderTimeline = (grouped, emptyText) => {
    if (!Object.keys(grouped).length) {
      return <p>{emptyText}</p>;
    }

    return (
      <div className="timeline-wrapper">
        {Object.entries(grouped).map(([date, items]) => (
          <div key={date} className="timeline-group">
            {/* DATE HEADER */}
            <div className="timeline-date">{date}</div>

            {items.map((a, i) => {
              const meta = getActivityMeta(a);
              const displayText =
                a.call_status && a.text?.includes("·")
                  ? a.text.split("·").slice(1).join("·").trim()
                  : a.text;
              const txRoute = a.source === "transaction" ? transactionActivityRoute(a, authInfo) : null;

              return (
                <div
                  key={i}
                  className="timeline-item"
                  style={txRoute ? { cursor: "pointer" } : undefined}
                  onClick={txRoute ? () => navigate(txRoute) : undefined}
                >
                  {/* LEFT ICON */}
                  <div
                    className="timeline-icon"
                    style={{ background: meta.color }}
                  >
                    {meta.icon}
                  </div>

                  {/* RIGHT CONTENT */}
                  <div className="timeline-content">
                    <div className="timeline-header">
                      {a.call_status && (
                        <span className="timeline-status">
                          {a.call_status}
                        </span>
                      )}

                      <span className="timeline-time-inline">
                        {convertToCompanyTimezoneObject(a.created_at).format("hh:mm A")}
                      </span>
                    </div>

                    {a.call_status && a.duration && (
                      <div className="timeline-meta">
                        Duration: {Math.floor(a.duration / 60)}m {a.duration % 60}s
                      </div>
                    )}

                    <div className="timeline-text">
                      {displayText}
                    </div>

                    <AttachmentPreviewList attachments={a.attachments || []} />

                    {a.follow_up_date && (
                      <div className="timeline-meta">
                        Follow-up:{" "}
                        {convertToCompanyTimezoneObject(a.follow_up_date).format("DD MMM YY hh:mm A")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderValue = (val) => {
  if (!val) return "-";

  if (Array.isArray(val)) return val.join(", ");

  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.join(", ");
    } catch {}
  }

  return val;
};

  // status and source editing and updating
  const startEdit = (field, currentValue) => {
    setEditingField(field);
    setTempValue(currentValue);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setTempValue(null);
  };

  const saveEdit = async () => {
    try {

      switch (editingField) {

        case "status":
          await updateLeadMetaApi({
            lead_id: lead.id,
            status_id: tempValue,
          });
          break;

        case "source":
          await updateLeadMetaApi({
            lead_id: lead.id,
            source_id: tempValue,
          });
          break;

        case "purpose":
          await updateLeadMetaApi({
            lead_id: lead.id,
            purpose_id: tempValue,
          });
          break;

        case "name":
          await updateLeadMetaApi({
            lead_id: lead.id,
            name: tempValue,
          });
          break;

        case "email":
          await updateLeadMetaApi({
            lead_id: lead.id,
            email: tempValue,
          });
          break;

        case "company":
          await updateLeadMetaApi({
            lead_id: lead.id,
            company: tempValue,
          });
          break;

        case "address":
          await updateLeadMetaApi({
            lead_id: lead.id,
            address: tempValue,
          });
          break;

        case "assigned_to":
          await updateLeadMetaApi({
            lead_id: lead.id,
            assigned_to: tempValue,
          });
          break;
      }

      if (editingField.startsWith("custom_")) {
        const field_id = editingField.replace("custom_", "");
        const field = customFields.find((f) => f.id === field_id);

        let valueToSave = tempValue;

        if (field?.type === "multi_select" && Array.isArray(tempValue)) {
          valueToSave = JSON.stringify(tempValue);
        }

        await updateLeadCustomFieldApi({
          lead_id: lead.id,
          field_id,
          value: valueToSave,
        });
      }

      message.success("Updated successfully");

      setEditingField(null);
      setTempValue(null);

      dispatch(fetchLeads());

    } catch {
      message.error("Update failed");
    }
  };

  // 🔹 Call action
  const handleCall = () => {
    if (!lead?.phone) return;
    window.location.href = `tel:${resolveDialableNumber(lead.phone, lead.country)}`;
  };

  // 🔹 WhatsApp action
  const handleWhatsApp = () => {
    if (!lead?.phone) return;
    window.open(`https://wa.me/${resolveWhatsAppNumber(lead.phone, lead.country)}`);
  };

  if (leadNotFound) {
    return (
      <Result
        status="404"
        title="Lead not found"
        subTitle="This lead may have been deleted, or you may not have access to it."
        extra={
          <Button type="primary" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!lead) return <p>Loading...</p>;

  const getStatusLabel = (status) => {
    if (status === "p") return "New";
    if (status === "a") return "Converted";
    if (status === "r") return "Lost";
    return status;
    };

    const getSourceLabel = (source) => {
    if (source === "account") return "Manual";
    if (source === "inquiry") return "Inquiry";
    return source;
    };

    const getColor = (a) => {
      if (a.source === "task") return "orange";
      if (a.source === "deal") return "purple";
      if (a.type === "call") return "blue";
      if (a.type === "system") return "geekblue";
      return "green";
    };

    const renderCustomFieldEditor = (field) => {
      const value = tempValue;

      switch (field.type) {
        case "text":
          return (
            <Input
              value={value || ""}
              style={{ width: 220 }}
              onChange={(e) => setTempValue(e.target.value)}
            />
          );

        case "number":
          return (
            <Input
              type="number"
              value={value ?? ""}
              style={{ width: 220 }}
              onChange={(e) => setTempValue(e.target.value)}
            />
          );

        case "dropdown":
          return (
            <Select
              value={value ?? undefined}
              style={{ width: 220 }}
              onChange={(v) => setTempValue(v)}
              options={(field.options || []).map((o) => ({
                label: typeof o === "string" ? o : o.label || o.value,
                value: typeof o === "string" ? o : o.value || o.label,
              }))}
            />
          );

        case "multi_select":
          return (
            <Select
              mode="multiple"
              value={
                Array.isArray(value)
                  ? value
                  : typeof value === "string" && value
                  ? (() => {
                      try {
                        return JSON.parse(value);
                      } catch {
                        return [];
                      }
                    })()
                  : []
              }
              style={{ width: 220 }}
              onChange={(v) => setTempValue(v)}
              options={(field.options || []).map((o) => ({
                label: typeof o === "string" ? o : o.label || o.value,
                value: typeof o === "string" ? o : o.value || o.label,
              }))}
            />
          );

        case "date":
          return (
            <DatePicker
              style={{ width: 220 }}
              value={value ? dayjs(value) : null}
              onChange={(d) =>
                setTempValue(d ? d.format("YYYY-MM-DD") : null)
              }
            />
          );

        case "datetime":
          return (
            <DatePicker
              showTime
              style={{ width: 220 }}
              value={value ? dayjs(value) : null}
              onChange={(d) =>
                setTempValue(d ? d.format("YYYY-MM-DD HH:mm:ss") : null)
              }
            />
          );

        default:
          return (
            <Input
              value={value || ""}
              style={{ width: 220 }}
              onChange={(e) => setTempValue(e.target.value)}
            />
          );
      }
    };

    

  return (
    <div style={{ padding: "12px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* REFACTORED RESPONSIVE HEADER BANNER */}
      <Card 
        style={{ 
          marginBottom: 16, 
          borderRadius: "12px", 
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #f0f0f0"
        }}
        bodyStyle={{ padding: "16px" }}
      >
        <Row gutter={[16, 16]} align="middle">
          {/* Context Wrapper */}
          <Col xs={24} sm={18}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {/* Back Navigation Action */}
              <div>
                <Button 
                  type="text" 
                  icon={<ArrowLeftOutlined />} 
                  onClick={() => navigate(-1)} 
                  style={{ paddingLeft: 0, color: "#8c8c8c", height: "auto" }}
                >
                  
                </Button>
              </div>
              
              {/* Responsive Header: Wraps column-wise on mobile, row-wise on desktop */}
              <div style={{ 
                display: "flex", 
                flexDirection: "row", 
                flexWrap: "wrap", 
                alignItems: "center", 
                gap: "12px" 
              }}>
                <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 600, color: "#1f1f1f", lineHeight: 1.2 }}>
                  {lead.name}
                </h1>
                <div style={{ display: "inline-flex", gap: "6px", alignItems: "center" }}>
                  <Tag color="blue" style={{ borderRadius: "4px", margin: 0 }}>
                    {getSourceLabel(lead.source)}
                  </Tag>
                  <Tag color={lead.status?.color || "default"} style={{ borderRadius: "4px", margin: 0 }}>
                    {lead.status?.name}
                  </Tag>
                </div>
              </div>

              {/* Contact Details Meta String */}
              <div style={{ color: "#595959", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                <UserOutlined style={{ color: "#bfbfbf" }} /> {formatPhone(lead.phone)}
                {lead.phone && (
                  <WhatsAppOutlined
                    style={{ color: "#25D366", cursor: "pointer", fontSize: "16px" }}
                    onClick={handleWhatsApp}
                  />
                )}
              </div>
            </div>
          </Col>

          {/* Strategic Call Trigger Area */}
          <Col xs={24} sm={6}>
            <Button 
              type="primary" 
              icon={<PhoneOutlined />} 
              size="large"
              block
              onClick={handleCall}
              style={{ 
                background: "#52c41a", 
                borderColor: "#52c41a", 
                borderRadius: "6px",
                fontWeight: 500,
                height: "44px"
              }}
            >
              Call Lead
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 🔥 TABS */}
      <Tabs
        defaultActiveKey="info"
        onChange={handleTabChange}
        size="large"
        tabBarStyle={{
          fontWeight: 500, marginBottom: "20px"
        }}
        items={[
          {
            key: "info",
            label: "Info",
            children: (
              // <Card>
              //   <p><b>Name:</b> {lead.name}</p>
              //   <p><b>Phone:</b> {formatPhone(lead.phone)}</p>
              //   <p><b>Email:</b> {lead.email || "-"}</p>
              //   <p><b>Company:</b> {lead.company || "-"}</p>
              //   <p><b>Profession:</b> {lead.profession || "-"}</p>
              //   <p><b>Country:</b> {lead.country || "-"}</p>
              //   {/* 🔥 CUSTOM FIELDS */}
              //   <div style={{ marginTop: 16 }}>
              //     <b>Additional Info</b>

              //     {customFields.map((field) => {
              //       const value = lead.custom_fields?.[field.field_key];

              //       return (
              //         <div key={field.id} style={{ marginTop: 8 }}>
              //           <span style={{ color: "#888" }}>
              //             {field.field_name}:
              //           </span>{" "}
              //           <span>{value || "-"}</span>
              //         </div>
              //       );
              //     })}
              //   </div>
              //   <p><b>Agent:</b> {lead.agent_name || "Unassigned"}</p>

              //   <Select
              //     style={{ width: "500px", marginBottom: 10 }}
              //     value={lead.status_id}
              //     onChange={async (val) => {
              //       await updateLeadMetaApi({
              //         lead_id: lead.id,
              //         status_id: val,
              //       });
              //       dispatch(fetchLeads());
              //     }}
              //     options={statusList?.map(s => ({
              //       label: s.name,
              //       value: s.id
              //     }))}
              //   />

              //   <p>
              //     <b>Status:</b>{" "}
              //     <Tag color={lead.status?.color || "default"}>
              //       {lead.status?.name}
              //     </Tag>
              //   </p>

              //   <Select
              //     style={{ width: "500px", marginBottom: 10 }}
              //     value={lead.source_id}
              //     onChange={async (val) => {
              //       await updateLeadMetaApi({
              //         lead_id: lead.id,
              //         source_id: val,
              //       });
              //       dispatch(fetchLeads());
              //     }}
              //     options={sourceList?.map(s => ({
              //       label: s.name,
              //       value: s.id
              //     }))}
              //   />

              //   <p>
              //     <b>Source:</b>{" "}
              //     <Tag>{lead.source}</Tag>
              //   </p>
              // </Card>
              <Card>
                <Row gutter={[16, 12]}>
                  <Col xs={24} md={12}>
                    <b>Name:</b>{" "}
                    {editingField === "name" ? (
                      <>
                        <Input
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          style={{ width: 220 }}
                        />

                        <CheckOutlined
                          onClick={saveEdit}
                          style={{ marginLeft: 8, color: "green" }}
                        />

                        <CloseOutlined
                          onClick={cancelEdit}
                          style={{ marginLeft: 8, color: "red" }}
                        />
                      </>
                    ) : (
                      <>
                        {lead.name}

                        <EditOutlined
                          className="crm-edit-icon"
                          style={{ marginLeft: 8 }}
                          onClick={() => startEdit("name", lead.name)}
                        />
                      </>
                    )}
                  </Col>
                  <Col xs={24} md={12}>
                    <b>Phone:</b> {formatPhone(lead.phone)}
                    {lead.phone && (
                      <WhatsAppOutlined
                        style={{ color: "#25D366", cursor: "pointer", marginLeft: 8 }}
                        onClick={handleWhatsApp}
                      />
                    )}
                  </Col>

                  {/* <Col  xs={24} md={12}><b>Email:</b> {lead.email || "-"}</Col> */}
                  <Col xs={24} md={12}>
                    <b>Email:</b>{" "}
                    {editingField === "email" ? (
                      <>
                        <Input
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          style={{ width: 220 }}
                        />

                        <CheckOutlined
                          onClick={saveEdit}
                          style={{ marginLeft: 8, color: "green" }}
                        />

                        <CloseOutlined
                          onClick={cancelEdit}
                          style={{ marginLeft: 8, color: "red" }}
                        />
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {lead.email}
                        </span>

                        <EditOutlined
                          className="crm-edit-icon"
                          style={{ marginLeft: 8 }}
                          onClick={() => startEdit("email", lead.email)}
                        />
                      </>
                    )}
                  </Col>
                  {/* <Col  xs={24} md={12}><b>Company:</b> {lead.company || "-"}</Col> */}
                  <Col xs={24} md={12}>
                    <b>Company:</b>{" "}
                    {editingField === "company" ? (
                      <>
                        <Input
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          style={{ width: 220 }}
                        />

                        <CheckOutlined
                          onClick={saveEdit}
                          style={{ marginLeft: 8, color: "green" }}
                        />

                        <CloseOutlined
                          onClick={cancelEdit}
                          style={{ marginLeft: 8, color: "red" }}
                        />
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {lead.company}
                        </span>

                        <EditOutlined
                          className="crm-edit-icon"
                          style={{ marginLeft: 8 }}
                          onClick={() => startEdit("company", lead.company)}
                        />
                      </>
                    )}
                  </Col>
                  {/* <Col  xs={24} md={12}><b>Address:</b> {lead.address || "-"}</Col> */}
                  <Col xs={24} md={12}>
                    <b>Address:</b>{" "}
                    {editingField === "address" ? (
                      <>
                        <Input
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          style={{ width: 220 }}
                        />

                        <CheckOutlined
                          onClick={saveEdit}
                          style={{ marginLeft: 8, color: "green" }}
                        />

                        <CloseOutlined
                          onClick={cancelEdit}
                          style={{ marginLeft: 8, color: "red" }}
                        />
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "anywhere"
                          }}
                        >
                          {lead.address}
                        </span>

                        <EditOutlined
                          className="crm-edit-icon"
                          style={{ marginLeft: 8 }}
                          onClick={() => startEdit("address", lead.address)}
                        />
                      </>
                    )}
                  </Col>
                  {lead.profession && (
                    <Col xs={24} md={12}>
                      <b>Profession:</b> {lead.profession || "-"}
                    </Col>
                  )}
                  {lead.country && (
                    <Col xs={24} md={12}>
                      <b>Country:</b> {lead.country || "-"}
                    </Col>
                  )}

                  {/* <Col  xs={24} md={12}><b>Agent:</b> {lead.agent_name || "Unassigned"}</Col> */}
                  <Col xs={24} md={12}>
                    <b>Agent:</b>{" "}
                    {editingField === "assigned_to" ? (
                      <>
                        <Select
                          style={{ width: 220 }}
                          value={tempValue}
                          onChange={setTempValue}
                          options={
                            adminUser
                              ? (users || []).map((u) => ({ label: u.full_name, value: u.id }))
                              : [
                                  { label: lead.agent_name, value: lead.assigned_to },
                                  ...myTeammates.map((u) => ({ label: u.full_name, value: u.id })),
                                ]
                          }
                        />

                        <CheckOutlined
                          onClick={saveEdit}
                          style={{ marginLeft: 8, color: "green" }}
                        />

                        <CloseOutlined
                          onClick={cancelEdit}
                          style={{ marginLeft: 8, color: "red" }}
                        />
                      </>
                    ) : (
                      <>
                        <span
                          style={{
                            wordBreak: "break-word",
                            overflowWrap: "anywhere"
                          }}
                        >
                          {lead.agent_name}
                        </span>
                        {(adminUser ||
                          (lead.assigned_to === authInfo?.userInfo?.user_id && myTeammates.length > 0)) && (
                          <EditOutlined
                            className="crm-edit-icon"
                            style={{ marginLeft: 8 }}
                            onClick={() =>
                              startEdit("assigned_to", lead.assigned_to)
                            }
                          />
                        )}
                      </>
                    )}
                  </Col>
                </Row>

                {/* 🔥 CUSTOM FIELDS */}
                <div style={{ marginTop: 20 }}>
                  <h4>Additional Info</h4>

                  <Row gutter={[16, 12]}>
                    {customFields.map((field) => {
                      const value = lead.custom_fields?.[field.field_key];
                      return (
                        <Col xs={24} md={12} key={field.id}>
                          <b>{field.field_name}:</b>{" "}
                          {editingField === `custom_${field.id}` ? (
                            <>
                              {renderCustomFieldEditor(field)}
                              <CheckOutlined
                                style={{
                                  marginLeft: 8,
                                  color: "green",
                                  cursor: "pointer",
                                }}
                                onClick={saveEdit}
                              />
                              <CloseOutlined
                                style={{
                                  marginLeft: 8,
                                  color: "red",
                                  cursor: "pointer",
                                }}
                                onClick={cancelEdit}
                              />
                            </>
                          ) : (
                            <>
                              {renderValue(value)}
                              <EditOutlined
                                className="crm-edit-icon"
                                style={{ marginLeft: 8 }}
                                onClick={() => startEdit(`custom_${field.id}`, value)}
                              />
                            </>
                          )}
                        </Col>
                      );
                    })}
                  </Row>

                  <Row style={{ marginTop: 16 }}>
                    <Col xs={24} md={12}>
                      <b>Status:</b>{" "}
                      {editingField === "status" ? (
                        <>
                          <Select
                            value={tempValue}
                            style={{ width: 180 }}
                            onChange={setTempValue}
                            options={statusList?.map((s) => ({
                              label: s.name,
                              value: s.id,
                            }))}
                          />

                          <CheckOutlined
                            style={{
                              marginLeft: 8,
                              color: "green",
                              cursor: "pointer",
                            }}
                            onClick={saveEdit}
                          />

                          <CloseOutlined
                            style={{
                              marginLeft: 8,
                              color: "red",
                              cursor: "pointer",
                            }}
                            onClick={cancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          <Tag color={lead.status?.color || "default"}>
                            {lead.status?.name}
                          </Tag>

                          <EditOutlined
                            className="crm-edit-icon"
                            style={{
                              marginLeft: 8,
                              cursor: "pointer",
                              color: "#999",
                            }}
                            onClick={() => startEdit("status", lead.status_id)}
                          />
                        </>
                      )}
                    </Col>
                  </Row>
                  <Row style={{ marginTop: 12 }}>
                    <Col xs={24} md={12}>
                      <b>Source:</b>{" "}
                      {editingField === "source" ? (
                        <>
                          <Select
                            value={tempValue}
                            style={{ width: 180 }}
                            onChange={setTempValue}
                            options={sourceList?.map((s) => ({
                              label: s.name,
                              value: s.id,
                            }))}
                          />

                          <CheckOutlined
                            style={{
                              marginLeft: 8,
                              color: "green",
                              cursor: "pointer",
                            }}
                            onClick={saveEdit}
                          />

                          <CloseOutlined
                            style={{
                              marginLeft: 8,
                              color: "red",
                              cursor: "pointer",
                            }}
                            onClick={cancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          <Tag>{lead.source}</Tag>

                          <EditOutlined
                            className="crm-edit-icon"
                            style={{ marginLeft: 8, cursor: "pointer" }}
                            onClick={() => startEdit("source", lead.source_id)}
                          />
                        </>
                      )}
                    </Col>
                  </Row>

                  <Row style={{ marginTop: 12 }}>
                    <Col xs={24} md={12}>
                      <b>Purpose:</b>{" "}
                      {editingField === "purpose" ? (
                        <>
                          <Select
                            value={tempValue}
                            style={{ width: 180 }}
                            onChange={setTempValue}
                            options={purposeList?.map((p) => ({
                              label: p.name,
                              value: p.id,
                            }))}
                          />

                          <CheckOutlined
                            style={{
                              marginLeft: 8,
                              color: "green",
                              cursor: "pointer",
                            }}
                            onClick={saveEdit}
                          />

                          <CloseOutlined
                            style={{
                              marginLeft: 8,
                              color: "red",
                              cursor: "pointer",
                            }}
                            onClick={cancelEdit}
                          />
                        </>
                      ) : (
                        <>
                          <Tag>
                            {lead?.purpose?.name || lead?.purpose || "-"}
                          </Tag>

                          <EditOutlined
                            className="crm-edit-icon"
                            style={{
                              marginLeft: 8,
                              cursor: "pointer",
                              color: "#999",
                            }}
                            onClick={() =>
                              startEdit("purpose", lead?.purpose_id)
                            }
                          />
                        </>
                      )}
                    </Col>
                  </Row>
                </div>
              </Card>
            ),
          },

          {
            key: "notes",
            label: "Notes",
            children: (
              <>
                <Card style={{ marginBottom: 10, borderRadius: 10 }}>
                  <Input.TextArea
                    className="crm-user-text"
                    style={{height:"auto"}}
                    autoSize={{
                      minRows: 3,
                      maxRows: 10,
                    }}
                    value={activityText}
                    onChange={(e) => setActivityText(e.target.value)}
                    placeholder="Add note..."
                  />

                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                    <Upload
                      multiple
                      accept=".png,.jpg,.jpeg,.pdf"
                      fileList={activityFileList}
                      beforeUpload={(file) => {
                        const isAllowed = [
                          "image/png",
                          "image/jpeg",
                          "application/pdf",
                        ].includes(file.type);

                        if (!isAllowed) {
                          message.error(
                            "Only PNG, JPEG or PDF files are allowed"
                          );
                          return Upload.LIST_IGNORE;
                        }

                        if (activityFileList.length >= 5) {
                          message.error("Up to 5 attachments per note");
                          return Upload.LIST_IGNORE;
                        }

                        return false;
                      }}
                      onChange={({ fileList: next }) => setActivityFileList(next)}
                      onRemove={(file) =>
                        setActivityFileList((prev) =>
                          prev.filter((f) => f.uid !== file.uid)
                        )
                      }
                      style={{ marginTop: 10, display: "block" }}
                    >
                      <Button icon={<PaperClipOutlined />} size="small">
                        Attach file
                      </Button>
                    </Upload>

                    <VoiceRecorder
                      disabled={activityFileList.length >= 5}
                      onRecorded={(file) => {
                        if (activityFileList.length >= 5) {
                          message.error("Up to 5 attachments per note");
                          return;
                        }
                        setActivityFileList((prev) => [
                          ...prev,
                          {
                            uid: `voice-${Date.now()}`,
                            name: file.name,
                            status: "done",
                            originFileObj: file,
                          },
                        ]);
                      }}
                    />
                  </div>

                  <Button
                    type="primary"
                    icon={<CommentOutlined />}
                    onClick={addNote}
                    loading={addingNote}
                    disabled={addingNote || (!activityText.trim() && !activityFileList.length)}
                    style={{ marginTop: 10 }}
                  >
                    Add Note
                  </Button>
                </Card>

                {renderTimeline(groupedNotes, "No notes yet")}
              </>
            ),
          },

          {
            key: "activity",
            label: "Activities",
            children: renderTimeline(groupedActivities, "No activity"),
          },

          // {
          //   key: "call",
          //   label: "Call",
          //   children: (
          //     <Card>
          //       <Select
          //         placeholder="Call Status"
          //         style={{ width: "100%", marginBottom: 10 }}
          //         onChange={setCallStatus}
          //         options={[
          //           { label: "Connected", value: "connected" },
          //           { label: "No Answer", value: "no_answer" },
          //           { label: "Missed", value: "missed" },
          //         ]}
          //       />

          //       <Select
          //         placeholder="Lead Status"
          //         style={{ width: "100%", marginBottom: 10 }}
          //         onChange={setLeadStatus}
          //         options={[
          //           { label: "New", value: "new" },
          //           { label: "Contacted", value: "contacted" },
          //           { label: "Interested", value: "interested" },
          //           { label: "Closed", value: "closed" },
          //         ]}
          //       />

          //       <TextArea
          //         rows={3}
          //         placeholder="Remarks"
          //         value={text}
          //         onChange={(e) => setText(e.target.value)}
          //       />

          //       <Button
          //         type="primary"
          //         block
          //         style={{ marginTop: 10 }}
          //         onClick={async () => {
          //           if (!callStatus) {
          //             return message.warning("Select call status");
          //           }

          //           await saveConversationApi({
          //             customer_id: id,
          //             text: `Call: ${callStatus} | ${text}`,
          //           });

          //           message.success("Call logged");
          //           setText("");
          //           setCallStatus(null);
          //           loadActivities();
          //         }}
          //       >
          //         Submit Call Feedback
          //       </Button>
          //     </Card>
          //   ),
          // },
          {
            key: "call",
            label: "Call",
            children: <CallTab lead={lead} deal={relatedDeals?.[0] || null} 
            onAfterSubmit={() => {
              loadActivities();
              loadTasks();
            }}
            />,
          },

          {
            key: "tasks",
            label: "Tasks",
            children: (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 12,
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setTaskDrawer(true)}
                  >
                    Add Task
                  </Button>
                </div>                
                {tasks.length ? (
                  tasks.map((task) => {
                    const isOverdue =
                      task.status !== "completed" && 
                      task.status !== "cancelled" &&
                      // dayjs(task.due_date).isBefore(dayjs());
                      convertToCompanyTimezoneObject(task.due_date).isBefore(
                        convertToCompanyTimezoneObject(new Date())
                      );
                    return (
                      <Card
                        key={task.task_id}
                        hoverable
                        onClick={() => navigate(`/crm/tasks/${task.task_id}`)}
                        style={{
                          marginBottom: 14,
                          borderRadius: 14,
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 16,
                          }}
                        >
                          {/* LEFT */}
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                fontSize: 16,
                                marginBottom: 4,
                              }}
                            >
                              {task.type}
                            </div>

                            <div
                              style={{
                                color: "#666",
                                marginBottom: 8,
                              }}
                            >
                              {task.remarks}
                            </div>

                            <div
                              style={{
                                fontSize: 12,
                                color: "#999",
                              }}
                            >
                              Scheduled:{" "}
                              {
                              // dayjs(task.due_date).format(
                              //   "DD MMM YYYY hh:mm A",
                              // )
                              convertToCompanyTimezoneObject(task.due_date).format(
                                "DD MMM YYYY hh:mm A",
                              )
                              // formatToCompanyTimezone(
                              //   task.due_date,
                              //   "DD MMM YYYY hh:mm A"
                              // )
                              }
                            </div>
                          </div>

                          {/* RIGHT */}
                          <div>
                            <Tag
                              color={
                                isOverdue
                                  ? "red"
                                  : task.status === "completed"
                                    ? "green"
                                  : task.status === "cancelled"
                                    ? "default"  
                                    : "orange"
                              }
                              style={{
                                paddingInline: 12,
                                borderRadius: 999,
                              }}
                            >
                              {isOverdue ? "Overdue" : (task.status.charAt(0).toUpperCase()+task.status.slice(1)) || "Pending"}
                            </Tag>
                          </div>
                        </div>
                      </Card>
                    );
                  })
                ) : (
                  <Card style={{ textAlign: "center", padding: 30 }}>
                    No tasks found
                  </Card>
                )}
              </div>
            ),
          },
          {
            key: "deals",
            label: "Deals",
            children: (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 12,
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setDealDrawer(true)}
                  >
                    Add Deal
                  </Button>
                </div>
                {relatedDeals.length ? (
                  relatedDeals.map((deal) => (
                    <Card
                      key={deal.deal_id}
                      hoverable
                      onClick={() => navigate(`/crm/deals/${deal.deal_id}`)}
                      style={{
                        marginBottom: 12,
                        borderRadius: 12,
                      }}
                    >
                      <Row gutter={[12, 12]}>
                        <Col span={24}>
                          <h3 style={{ marginBottom: 0 }}>{deal.title}</h3>
                        </Col>

                        <Col xs={24} sm={12}>
                          <b>Amount:</b> {currency}{" "}
                          {Number(deal.value || 0).toLocaleString()}
                        </Col>

                        <Col xs={24} sm={12}>
                          <b>Stage:</b>{" "}
                          <Tag color={stageColor[deal.stage] || "default"}>
                            {deal.stage}
                          </Tag>
                        </Col>

                        <Col xs={24} sm={12}>
                          <b>Expected Close:</b>{" "}
                          {deal.expected_close_date
                            ? 
                            // dayjs(deal.expected_close_date).format(
                            //     "DD MMM YYYY",)
                            convertToCompanyTimezoneObject(deal.expected_close_date).format(
                                "DD MMM YYYY",)
                            // formatToCompanyTimezone(
                            //   deal.expected_close_date,
                            //   "DD MMM YYYY"
                            // )
                            : "-"}
                        </Col>

                        <Col xs={24} sm={12}>
                          <b>Activities:</b> {deal.activity_count || 0}
                        </Col>
                      </Row>
                    </Card>
                  ))
                ) : (
                  <Card style={{ textAlign: "center", padding: 30 }}>
                    No deals found
                  </Card>
                )}
              </div>
            ),
          },
        ]}
      />
      <TaskDrawer
        open={taskDrawer}
        onClose={() => setTaskDrawer(false)}
        defaultLeadId={id}
        leads={list || []}
        deals={dealList || []}
        onSuccess={() => {
          loadTasks();
          loadActivities();
          setTaskDrawer(false);
        }}
      />

      <CreateDealDrawer
        open={dealDrawer}
        onClose={() => setDealDrawer(false)}
        selectedLead={lead}
        onSubmit={() => {
          dispatch(fetchDeals());
          setDealDrawer(false);
        }}
      />
    </div>
  );
};

export default LeadDetail;